<template>
  <UITabContainer
    v-if="tabs.length"
    class="certificates-tab-container"
    :tabs="tabs"
    @tabChanged="onTabChanged"
    data-cy="certificates-tab-container"
  >
    <template v-slot:tabHeaderContent>
      <div class="certificates-tab-header">
        <label>Plant</label>
        <MultiSelect
          class="certificates-tab-header__select"
          :items="plants"
          v-model:value="selectedPlants"
          :auto-close="false"
          placeholder="Select Plants"
          itemsName="Plants"
          @close="plantSelectionClose"
          @tag-clear-click="plantTagClearClick"
          data-cy="plant-select"
        />
        <label>Activity Date Range</label>
        <DropDownList
          class="certificates-tab-header__dropdown"
          :data-items="dateRangeValues"
          :text-field="'text'"
          v-model="selectedDateRange"
          placeholder="All"
          data-cy="date-range-select"
        />
      </div>
    </template>
    <template
      v-for="item in companySummary?.LabelTypes ||
      main.companySummary!.LabelTypes"
      :key="item"
      v-slot:[item.toString()]
    >
      <div>
        <KosherCertificatesTable
          v-if="viewLoaded"
          :plants="selectedTablePlants"
          :daysBack="selectedDateRange.daysBack"
          :companySummary="companySummary || main.companySummary!"
          :labelType="item"
        />
      </div>
    </template>
  </UITabContainer>
</template>

<script setup lang="ts">
import KosherCertificatesTable from "./KosherCertificatesTable.vue";
import UITabContainer from "@/shared/ui/tabs/tab-container.vue";
import { DropDownList } from "@progress/kendo-vue-dropdowns";
import { computed, onBeforeMount, ref, watchEffect } from "vue";
import { useMainStore } from "@/stores/useMainStore";
import type { IdAndName } from "@/Domain/IdAndName";
import type { CompanySummary } from "@/Domain/CompanySummary";
import MultiSelect from "@/shared/ui/inputs/MultiSelect.vue";
import { EnumCompanyStatus } from "@/Domain/EnumCompanyStatus";
import { CacheService } from "@/shared/services/CacheService";
import { createCacheKey } from "@/shared/helpers/cacheHelper";
import { api } from "@/api";
import { handleResponsePromiseAsync } from "@/shared/helpers/apiHelper";

const main = useMainStore();

const props = defineProps<{
  companySummary?: CompanySummary;
}>();

const plants = ref<IdAndName[]>([]);
const selectedPlants = ref<IdAndName[]>([...plants.value]);
const selectedTablePlants = ref<IdAndName[]>([...selectedPlants.value]);
const viewLoaded = ref(false);

const dateRangeValues = [
  { text: "All", daysBack: 0 },
  { text: "Yesterday", daysBack: 1 },
  { text: "Last 7 Days", daysBack: 7 },
  { text: "Last 30 Days", daysBack: 30 },
];

const tabs = computed(() => {
  return (
    (props.companySummary?.LabelTypes || main.companySummary!.LabelTypes)?.map(
      (lt) => {
        return { id: lt.toString(), label: lt.toString() };
      }
    ) || []
  );
});

const selectedTabId = ref(tabs.value[0]?.id || "");

const selectedDateRange = ref(dateRangeValues[0]);

const plantTagClearClick = function () {
  selectedTablePlants.value = [...selectedPlants.value];
};

const plantSelectionClose = function () {
  selectedTablePlants.value = [...selectedPlants.value];
};

const updatePlantListAsync = async function () {
  const companyPlants =
    props.companySummary?.AuthorizedOwnsIds ??
    main.companySummary!.AuthorizedOwnsIds;

  let labelPlantIds = [] as string[];

  const parameters = {
    userLogin: main.userLogin,
    companyLogin: main.companyLogin,
    searchString: "",
    labelType: selectedTabId.value,
    plantList: companyPlants.map((p) => p.ID).join(";"),
    daysBack: 0,
    groupByLabel: true,
    filters: "",
    skip: 0,
    take: 999999,
    sortField: "",
    ascending: false,
    valuesForFieldOnly: "OwnsId",
  };

  const plantPromise = CacheService.getResponseAsync(
    createCacheKey(parameters, "ProductDisplayPlants"),
    () =>
      api.get(
        "GetProductDisplayByCriteriaWithFilteringValuesForFieldOnly",
        parameters
      )
  );

  await handleResponsePromiseAsync(
    plantPromise,
    (data) => {
      labelPlantIds = data.ListItems;
    },
    "KosherCertificatesView",
    "updatePlantListAsync"
  );

  // 11018: only certified plants
  // 13573: only plants correlated to selected label type
  plants.value = (
    props.companySummary?.AuthorizedOwnsIds ??
    main.companySummary!.AuthorizedOwnsIds
  )
    .filter(
      (p) =>
        !p.OnHold &&
        p.Status == EnumCompanyStatus.Certified &&
        labelPlantIds.includes(p.ID.toString())
    )
    .sort((a, b) => a.Name.localeCompare(b.Name));
};

const onTabChanged = async function (tabId: string) {
  console.debug(`KosherCertificatesView.vue: onTabChanged(), tabId:`, tabId);
  viewLoaded.value = false;

  selectedTabId.value = tabId;
  await updatePlantListAsync();

  selectedPlants.value = [...plants.value];
  selectedTablePlants.value = [...selectedPlants.value];
  selectedDateRange.value = dateRangeValues[0];

  viewLoaded.value = true;
};

onBeforeMount(async () => {
  await updatePlantListAsync();
  selectedPlants.value = [...plants.value];
  selectedTablePlants.value = [...selectedPlants.value];
  viewLoaded.value = true;
});
</script>

<style lang="scss">
.certificates-tab-container {
  .certificates-tab-header {
    &__select {
      width: 400px;
      margin-right: 10px;
    }

    &__dropdown {
      width: 200px;
    }
  }
}
</style>

<style scoped lang="scss">
.certificates-tab-container {
  margin: 0 24px;

  .certificates-tab-header {
    display: flex;
    position: relative;
    align-items: center;
    margin-left: auto;

    label {
      font-weight: normal;
      font-size: 12px;
      margin-right: 10px;
      color: #999;
      margin-bottom: 0;
    }
  }
}
</style>
